/**
 * Page FAQs — one array, rendered AND described (ADR 0142, SEO parity G4).
 *
 * The static marketing pages (services, the calculators, home) keep their FAQ
 * copy in web i18n and have no backend RPC, so — unlike the entity pages, whose
 * JSON-LD is server-built in the Rust SEO SSOT — their FAQ structured data is
 * assembled here and handed to `SeoHead`'s `structuredData` (which injects each
 * string verbatim inside a `<script type="application/ld+json">`).
 *
 * ## Why `faqPageJsonLd` is not exported any more
 *
 * Every consumer used to write the same two independent things: a
 * `faqPageJsonLd(faqItems(t.dyn, t.indices('faqs.list')))` for the head, and a
 * separate `t.indices('faqs.list').map(…)` accordion for the body — each with a
 * comment promising the two "can't drift". Two assertions about one thing, held
 * together by prose.
 *
 * They are now ONE value. {@link pageFaqs} is the only door onto `FAQPage`
 * JSON-LD, it hands back the items alongside it, and `<FaqDisclosureList>`
 * accepts nothing but that pair — so describing one FAQ set to crawlers while
 * rendering another is unrepresentable rather than merely discouraged.
 *
 * ## Entity pages join the same shape ({@link faqsFromWire})
 *
 * The estate page's FAQ is NOT i18n copy — its answers are whole sentences the
 * Rust SEO SSOT derives from stored facts ("{station} station is about
 * {distance} m from {estate} in a straight line"). That `FAQPage` used to
 * arrive inside `SeoData.structured_data`, so `<SeoHead {...seo} />` emitted
 * the claim automatically while the page rendered a facts grid that never said
 * those sentences: the same defect as the accordion, reached from the other
 * end. The pair now travels as one `seo.v1.SeoFaq` message and lands here
 * as the same {@link PageFaqs} the static pages carry, so entity and static
 * FAQs are held to one binding by one rule.
 */
import type { SeoFaq } from '@letsgethome/proto/seo/v1/seo_pb';
import { plainText, type LocalizedText } from '@letsgethome/types/branded';

export interface FaqItem {
  readonly question: LocalizedText;
  readonly answer: LocalizedText;
}

/**
 * A page's FAQ set: the items to render, and the `FAQPage` JSON-LD describing
 * exactly those items. Minted only by {@link pageFaqs} / {@link faqsFromCatalog},
 * so the two halves are the same array by construction.
 */
export interface PageFaqs {
  readonly items: readonly FaqItem[];
  readonly jsonLd: string;
}

/** A `t.dyn` narrowed to the FAQ keys at exactly the indices `I` — a page's
 *  namespaced `t.dyn` is assignable here only when its namespace really has a
 *  `faqs.list[]` covering them, matching `t.dyn`'s namespace-prefixing. */
type FaqTranslate<I extends number> = (
  key: `faqs.list.${I}.question` | `faqs.list.${I}.answer`
) => LocalizedText;

/**
 * A complete `FAQPage` JSON-LD object as a compact string.
 *
 * Deliberately module-private: the only way to obtain one is {@link pageFaqs},
 * which also hands back the items the page is then obliged to render.
 */
function faqPageJsonLd(items: readonly FaqItem[]): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  });
}

/**
 * Pair an FAQ array with the structured data describing it — the single door
 * onto `FAQPage` JSON-LD.
 */
export function pageFaqs(items: readonly FaqItem[]): PageFaqs {
  return { items, jsonLd: faqPageJsonLd(items) };
}

/**
 * The FAQs a page keeps under the `faqs.list.{i}.{question,answer}` i18n
 * convention, paired with their structured data.
 *
 * `indices` is the CATALOG's own index tuple (`t.indices('faqs.list')`), not a
 * count: the JSON-LD then covers exactly the FAQs the catalog defines and
 * exactly the ones the page renders from the same tuple. Taking a `count:
 * number` here — as this did — let every caller restate the list length by hand
 * next to an unbounded `faqs.list.${number}` key type, which is the shape that
 * put a raw message id on the live homepage.
 */
export function faqsFromCatalog<I extends number>(
  tDyn: FaqTranslate<I>,
  indices: readonly I[]
): PageFaqs {
  return pageFaqs(
    indices.map((i) => ({
      question: tDyn(`faqs.list.${i}.question`),
      answer: tDyn(`faqs.list.${i}.answer`),
    }))
  );
}

/**
 * An entity page's server-built FAQ (`SeoData.faq`) as a {@link PageFaqs}.
 *
 * The JSON-LD is the SERVER's document, not a re-mint: the Rust SEO SSOT built
 * it from these very items through its own paired door
 * (`jsonld::faq_section`), where the schema.org vocabulary is checked against
 * the vendored release. Re-minting here would put a second, unchecked
 * assertion about the same answers on the page — the exact duplication this
 * module exists to remove — so the pair is carried, not rebuilt.
 *
 * `undefined` for an unset or empty message: a page with no answerable
 * questions must emit NO `FAQPage`, since an empty one is a structured-data
 * error. The strings are backend-resolved data, so they mint `LocalizedText`
 * via `plainText` — the same treatment as `SeoData`'s title and breadcrumb
 * labels.
 */
export function faqsFromWire(faq: SeoFaq | undefined): PageFaqs | undefined {
  if (!faq || faq.items.length === 0 || !faq.jsonLd) return undefined;
  return {
    items: faq.items.map((item) => ({
      question: plainText(item.question),
      answer: plainText(item.answer),
    })),
    jsonLd: faq.jsonLd,
  };
}
